import React, { use } from 'react';
import { useParams, Navigate } from 'react-router';
import type { OrderType } from '@lds-efsp/shared-contracts';
import { useBrand } from '../../../app/config/brand';
import { useAuthStatus } from '../../../auth/useAuthStatus';
import { OrderFormProvider } from '../contexts/OrderFormContext';
import OrderTypeSelector from '../components/OrderTypeSelector';
import OrderAccountBanner from '../components/OrderAccountBanner';
import AdminCompanyContext from '../../../shared/contexts/AdminCompanyContext';
import { withFreshStartSignal } from '../../efiling/lib/resolve-entry-intent';

// Form imports — simple forms
import CourtesyCopyForm from '../forms/CourtesyCopyForm';
import SecretaryOfStateForm from '../forms/SecretaryOfStateForm';
import CountyRecordingForm from '../forms/CountyRecordingForm';
import EServiceForm from '../forms/EServiceForm';
import SkipTraceForm from '../forms/SkipTraceForm';

// Form imports — complex forms
import ProcessServingForm from '../forms/ProcessServingForm';
import CourtFilingForm from '../forms/CourtFilingForm';
import DocumentRetrievalForm from '../forms/DocumentRetrievalForm';

const VALID_ORDER_TYPES: OrderType[] = [
  'e-filing', 'court-filing', 'process-serving', 'document-retrieval',
  'courtesy-copy-delivery', 'secretary-of-state-filing', 'county-recording',
  'skip-trace', 'eservice', 'concierge',
];

const ORDER_TYPE_LABELS: Record<string, string> = {
  'court-filing': 'Court Filing',
  'process-serving': 'Process Serving',
  'document-retrieval': 'Document Retrieval',
  'courtesy-copy-delivery': 'Courtesy Copy Delivery',
  'secretary-of-state-filing': 'Secretary of State Filing',
  'county-recording': 'County Recording',
  'skip-trace': 'Skip Trace',
  'eservice': 'eService',
  'concierge': 'Concierge Service',
};

function renderForm(orderType: OrderType, brand: ReturnType<typeof useBrand>) {
  switch (orderType) {
    case 'courtesy-copy-delivery':
      return <CourtesyCopyForm brand={brand} />;
    case 'secretary-of-state-filing':
      return <SecretaryOfStateForm brand={brand} />;
    case 'county-recording':
      return <CountyRecordingForm brand={brand} />;
    case 'eservice':
      return <EServiceForm brand={brand} />;
    case 'skip-trace':
      return <SkipTraceForm brand={brand} />;
    case 'process-serving':
      return <ProcessServingForm brand={brand} />;
    case 'court-filing':
      return <CourtFilingForm brand={brand} />;
    case 'document-retrieval':
      return <DocumentRetrievalForm brand={brand} />;
    case 'concierge':
      return (
        <div className="text-center py-12">
          <p className="text-lg font-medium" style={{ color: brand.colors.text }}>
            Concierge Service
          </p>
          <p className="mt-2" style={{ color: brand.colors.textLight }}>
            Coming soon. Please contact us to place a concierge order.
          </p>
        </div>
      );
    default:
      return null;
  }
}

const NewOrderPage: React.FC = () => {
  const { type } = useParams<{ type?: string }>();
  const brand = useBrand();
  const { isPlatformUser } = useAuthStatus();
  const adminCompany = use(AdminCompanyContext);

  // Platform users must pick an org before interacting with the form
  const formBlocked = isPlatformUser && !adminCompany?.selectedCompanyId;

  // No type selected — show type selector
  if (!type) {
    return (
      <div className="p-6">
        <h1
          className="text-2xl font-bold mb-6"
          style={{ color: brand.colors.text }}
        >
          New Order
        </h1>
        <OrderTypeSelector brand={brand} />
      </div>
    );
  }

  // eFiling redirects to the existing wizard. This is a fresh-start launch site:
  // feed the signalled path (and no ?orderId=) to the <Navigate> `to` prop so the
  // wizard resolves on entry as a fresh start (R6).
  if (type === 'e-filing') {
    return <Navigate to={withFreshStartSignal('/eFiling')} replace />;
  }

  // Invalid type
  if (!VALID_ORDER_TYPES.includes(type as OrderType)) {
    return <Navigate to="/orders/new" replace />;
  }

  const orderType = type as OrderType;

  return (
    <OrderFormProvider orderType={orderType} accountId={adminCompany?.selectedCompanyId}>
      <div className="p-6">
        <OrderAccountBanner />
        <h1
          className="text-2xl font-bold mb-6"
          style={{ color: brand.colors.text }}
        >
          {ORDER_TYPE_LABELS[orderType] ?? orderType} — New Order
        </h1>
        <div
          style={formBlocked ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
          aria-disabled={formBlocked}
        >
          {renderForm(orderType, brand)}
        </div>
      </div>
    </OrderFormProvider>
  );
};

export default NewOrderPage;
